import {
  EngagementSubmissionData,
  StageGetSubmissionsDocument,
  StageGetSubmissionsQuery,
  useOnMockStageEngagementSubmissionsChangedSubscription,
  useOnStageEngagementSubmissionsChangedSubscription,
  useStageGetSubmissionsQuery,
} from '@/generated/graphql';
import { createLogger } from '@/utils/createLogger';
import { TypedStageEngagement } from '@/utils/engagementTypeUtils';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';

const logger = createLogger('useStageEngagementSubmissions');

/**
 * Fetches the submissions for a stage engagement and updates the cache
 * when the submissions change via a subscription.
 */
export function useStageEngagementSubmissions<
  T extends EngagementSubmissionData,
>({ engagementId }: { engagementId: string }) {
  const { data, loading, error } = useStageGetSubmissionsQuery({
    variables: { engagementId },
    onError: (error) => {
      logger.error('Error fetching submissions', error);
    },
    onCompleted: () => {
      logger.info('Submissions fetched');
    },
  });

  const mockRealtime = useMockRealtimeChannel();

  useOnStageEngagementSubmissionsChangedSubscription({
    variables: { engagementId },
    onData: ({ client, data }) => {
      logger.info('Submissions changed', data);
      const nextSubmissions =
        data?.data?.engagementSubmissionsChanged?.submissions;
      if (!nextSubmissions) {
        logger.error('No submissions found');
        return;
      }

      logger.info('Updating cache with new submissions');
      logger.debug('nextSubmissions', nextSubmissions);
      client.cache.updateQuery<StageGetSubmissionsQuery>(
        {
          query: StageGetSubmissionsDocument,
          variables: { engagementId },
        },
        () => ({
          submissions: nextSubmissions,
        }),
      );
    },
    onError: (error) => {
      logger.error('subscription error', error);
    },
  });
  useOnMockStageEngagementSubmissionsChangedSubscription({
    skip: !mockRealtime,
    variables: { engagementId },
    onData: ({ client, data }) => {
      logger.info('Mock submissions changed', data);
      const nextSubmissions =
        data?.data?.mockEngagementSubmissionsChanged?.submissions;
      if (!nextSubmissions) {
        logger.error('No submissions found');
        return;
      }
      client.cache.updateQuery<StageGetSubmissionsQuery>(
        {
          query: StageGetSubmissionsDocument,
          variables: { engagementId },
        },
        () => ({
          submissions: nextSubmissions,
        }),
      );
    },
    onError: (error) => {
      logger.error('subscription error', error);
    },
  });

  return { data: data as TypedStageEngagement<any, any, T>, loading, error };
}
